import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { NumberInputWithSuffix } from '@flipster/features/src/input/number-input-with-suffix';
import { FormattedMessage } from '@flipster/i18n';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import ErrorMessage from '../ErrorMessage';
import { addPositionModalBunja } from '../state';
import { addPositionValidationBunja } from '../validation-state';
import { addPositionStepBunja } from './state';

const Dropdown = createDropdownSelect<'qty' | 'positionSize' | 'margin'>();
const orderByTokenMap = { margin: 'margin', positionSize: 'position_size', qty: 'quantity' } as const;

const ValueInputSection = () => {
  const t = useTranslations();
  const { instrumentAtom, inputValueAtom, inputModeAtom, targetPrecisionAtom, targetUnitAtom, positionAtom } =
    useBunja(addPositionModalBunja);
  const { maxAvailableMarginAtom, marginInputErrorAtom } = useBunja(addPositionValidationBunja);
  const { updateValueByPercent } = useBunja(addPositionStepBunja);
  const [inputValue, setInputValue] = useAtom(inputValueAtom);
  const [inputMode, setInputMode] = useAtom(inputModeAtom);
  const instrument = useAtomValue(instrumentAtom);
  const position = useAtomValue(positionAtom);
  const maxAvailableMargin = useAtomValue(maxAvailableMarginAtom);
  const marginInputError = useAtomValue(marginInputErrorAtom);
  const precision = useAtomValue(targetPrecisionAtom);
  const targetUnit = useAtomValue(targetUnitAtom);
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

  const handleInputModeChange = (mode: 'qty' | 'positionSize' | 'margin') => {
    setInputMode(mode);
    segment.track(TradeEvent.AddPositionMethodSelectClick, {
      symbol: position.symbol,
      side: position.side === 'Long' ? 'buy' : 'sell',
      leverage: position.initLeverage,
      order_by_token: orderByTokenMap[mode],
    });
  };

  const handleClickPercent = (percent: number) => {
    updateValueByPercent(percent);
  };

  const inputTitleLabel = {
    qty: t('Position_add_position.r250401.Input_position_quantity_title'),
    positionSize: t('Position_add_position.r250827.Input_position_quote_currency_title', {
      quoteCurrency: instrument.quoteCurrency,
    }),
    margin: t('Position_add_position.r250827.Input_margin_title', {
      quoteCurrency: instrument.quoteCurrency,
    }),
  };

  return (
    <div className={stack({ bg: 'background.default.level-1', px: 16, py: 12, gap: 8, borderRadius: 8 })}>
      <div className={stack({ gap: 6 })}>
        <Dropdown.Root selected={inputMode} onChange={handleInputModeChange}>
          <Dropdown.Trigger css={hstack.raw({ gap: 4, textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
            {inputTitleLabel[inputMode]}
            <ChevronDownIcon
              size={18}
              className={css({
                transition: 'transform 0.2s',
                '[aria-expanded=true] &': { transform: 'rotate(-180deg)' },
              })}
            />
          </Dropdown.Trigger>
          <Dropdown.Select portal css={{ w: 205 }}>
            <Dropdown.Item
              value='qty'
              label={
                <div>
                  <div className={dropdownHeaderStyle}>{inputTitleLabel['qty']}</div>
                  <div className={dropdownDescStyle}>
                    <FormattedMessage id='Position_add_position.r250401.Input_position_quantity_description' />
                  </div>
                </div>
              }
            />
            <Dropdown.Item
              value='positionSize'
              label={
                <div>
                  <div className={dropdownHeaderStyle}>{inputTitleLabel['positionSize']}</div>
                  <div className={dropdownDescStyle}>
                    <FormattedMessage id='Position_add_position.r250401.Input_position_usdt_description' />
                  </div>
                </div>
              }
            />
            <Dropdown.Item
              value='margin'
              label={
                <div>
                  <div className={dropdownHeaderStyle}>{inputTitleLabel['margin']}</div>
                  <div className={dropdownDescStyle}>
                    <FormattedMessage id='Position_add_position.r250401.Input_margin_description' />
                  </div>
                </div>
              }
            />
          </Dropdown.Select>
        </Dropdown.Root>
        <div className={stack({ gap: 4 })}>
          <div className={css({ h: 28 })}>
            <NumberInputWithSuffix
              placeholder={inputTitleLabel[inputMode]}
              value={inputValue}
              precision={precision}
              onChange={setInputValue}
              suffix={inputValue !== '' && <div className={css({ textStyle: 'Text/L/Strong' })}>{targetUnit}</div>}
              className={flex({
                textStyle: 'Title/M/Monospace',
                color: 'content.default.level-5',
                align: 'flex-end',
                gap: 4,
              })}
            />
          </div>
          {marginInputError && (
            <ErrorMessage
              type={marginInputError.priority}
              message={marginInputError.message}
              action={marginInputError.action}
            />
          )}
        </div>
      </div>
      <div
        className={hstack({
          py: 8,
          gap: 8,
          '& > button': {
            flex: 1,
            bg: 'background.default.level-3',
            color: 'content.default.level-5',
            textStyle: 'Text/S/Strong',
            h: 41,
            borderRadius: 4,
            display: 'flex',
            flexDir: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            px: 10,
          },
        })}
      >
        <button onClick={() => handleClickPercent(25)}>25%</button>
        <button onClick={() => handleClickPercent(50)}>50%</button>
        <button onClick={() => handleClickPercent(75)}>75%</button>
        <button onClick={() => handleClickPercent(100)}>
          <div>100%</div>
          {inputMode === 'margin' && (
            <div className={css({ whiteSpace: 'nowrap', textStyle: 'Text/XS/Monospace/Regular' })}>
              {t('Position_add_funds.r250828.Max_amount_quote_currency', {
                amount: numberFormatter(maxAvailableMargin, { precision: quoteCurrencyPrecision, toFixed: true }),
                quoteCurrency: instrument.quoteCurrency,
              })}
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default ValueInputSection;

const dropdownHeaderStyle = css({
  textStyle: 'Text/M/Regular',
  color: 'content.default.level-5',
});

const dropdownDescStyle = css({
  textStyle: 'Text/S/Regular',
  color: 'content.default.level-2',
});
